import React from "react";
import { useRecordContext } from "react-admin";
import CustomReferenceField from "../Common/CustomReferenceField";
import { Box, Chip } from "@mui/material";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { formatSlotSize } from "../../utils/FormatSlotSize";
import {
  HorizontalContainer,
  HorizontalLine,
  SectionHeader,
  SectionTitle,
  ShowLabel,
  ShowValue,
  StatusBooleanBadge,
  ThreeColGrid,
  WorkspacePanel,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { colors } from "../../styles/colors";
import { CalendarIcon, RecieptIcon } from "../Svg";

const CouponSkuMetaShowContent = () => {
  const record = useRecordContext();

  return (
    <WorkspacePanel style={{ marginTop: "16px" }}>

      {/* ── Header ── */}
      <WorkspaceSection>
        <SectionHeader>
          <WorkSpaceTitleRow>
            <RecieptIcon color={colors.primary} height={24} width={24} />
            <SectionTitle>Coupon SKU Details</SectionTitle>
          </WorkSpaceTitleRow>
        </SectionHeader>
      </WorkspaceSection>

      {/* ── Identity — short values, one line each ── */}
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>ID:</ShowLabel>
          <ShowValue>{record?.id ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Name:</ShowLabel>
          <ShowValue>{record?.name ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Pack Tag:</ShowLabel>
          <ShowValue>{record?.packTag ?? "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      <HorizontalLine />

      {/* Description — full width (value can be long) */}
      <HorizontalContainer style={{ marginBottom: "8px" }}>
        <ShowLabel style={{ marginBottom: "0px" }}>Description:</ShowLabel>
        <ShowValue>{record?.description ?? "-"}</ShowValue>
      </HorizontalContainer>

      <HorizontalLine />

      {/* ── Pricing — short values, one line each ── */}
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Price:</ShowLabel>
          <ShowValue>{record?.price ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Value:</ShowLabel>
          <ShowValue>{record?.value ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Coupon Type:</ShowLabel>
          <ShowValue>{record?.couponType ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Product Type:</ShowLabel>
          <ShowValue>{record?.productType ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Min. Order Value:</ShowLabel>
          <ShowValue>{record?.minimumOrderValue ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Max. Discount:</ShowLabel>
          <ShowValue>{record?.maximumDiscountAmount ?? "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      <HorizontalLine />

      {/* ── Limits ── */}
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>No of People:</ShowLabel>
          <ShowValue>{record?.noOfPeople ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Slot Size:</ShowLabel>
          <ShowValue>{formatSlotSize(record?.slotSize)}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Number of Days:</ShowLabel>
          <ShowValue>{record?.numberOfDays ?? "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Max. Usage Limit:</ShowLabel>
          <ShowValue>{record?.maximumUsageLimit ?? "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      <HorizontalLine />

      {/* ── Flags ── */}
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Is Active:</ShowLabel>
          <StatusBooleanBadge active={!!record?.isActive}>
            {record?.isActive ? "Yes" : "No"}
          </StatusBooleanBadge>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Is Popular:</ShowLabel>
          <StatusBooleanBadge active={!!record?.isPopular}>
            {record?.isPopular ? "Yes" : "No"}
          </StatusBooleanBadge>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Is Renewable:</ShowLabel>
          {record?.isRenewable == null ? (
            <ShowValue>-</ShowValue>
          ) : (
            <StatusBooleanBadge active={!!record?.isRenewable}>
              {record?.isRenewable ? "Yes" : "No"}
            </StatusBooleanBadge>
          )}
        </HorizontalContainer>
      </ThreeColGrid>

      <HorizontalLine />

      {/* ── Meta ── */}
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Created By:</ShowLabel>
          <CustomReferenceField listName="AdminUsers" source="createdBy" />
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Created At:</ShowLabel>
          <ShowValue>{record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Updated At:</ShowLabel>
          <ShowValue>{record?.updatedAt ? formatDateTimeWithSeconds(record.updatedAt) : "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      {/* ── Time Inclusion/Exclusion Rule ── */}
      {record?.timeInclusionExclusionRule && (
        <>
          <HorizontalLine />
          <WorkspaceSection>
            <SectionHeader>
              <WorkSpaceTitleRow>
                <CalendarIcon color={colors.primary} height={20} width={20} />
                <SectionTitle>Time Inclusive / Exclusive Rule</SectionTitle>
              </WorkSpaceTitleRow>
            </SectionHeader>
          </WorkspaceSection>
          <HorizontalContainer style={{ marginBottom: "8px" }}>
            <ShowLabel style={{ marginBottom: "0px" }}>Type:</ShowLabel>
            <ShowValue>{record?.timeInclusionExclusionRule?.type ?? "-"}</ShowValue>
          </HorizontalContainer>
          {record?.timeInclusionExclusionRule?.timeSlots?.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              {record.timeInclusionExclusionRule.timeSlots.map((slot: any, index: number) => (
                <Chip key={index} color="primary" label={slot} />
              ))}
            </Box>
          )}
        </>
      )}

    </WorkspacePanel>
  );
};

const CouponSkuMetaShow = () => {
  return <CouponSkuMetaShowContent />;
};

export default CouponSkuMetaShow;
